import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { API_BASE } from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';
import { downloadAuthenticatedUrl } from '../utils/authenticatedBlobDownload';
import { useAuth } from '../context/AuthContext';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import DateTimeInput from './ui/datetime-input';
import { validateDateTime } from '../utils/dateValidation';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Download } from 'lucide-react';

export default function AssignmentsStaff() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { showBranchLabels, formatLabel } = useBranchLabels();
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ title: '', class_id: '', subject_id: '', due_date: '', description: '' });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const attachmentInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [dueDateError, setDueDateError] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewForm, setReviewForm] = useState({ marks: '', feedback: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const getSelectedAcademicYearId = useCallback(() => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  }, [user?.school_id]);

  const load = useCallback(async () => {
    const academicYearId = getSelectedAcademicYearId();
    const classParams = academicYearId ? { academic_year_id: academicYearId } : {};
    try {
      const [aRes, cRes] = await Promise.all([
        api.get('/assignments'),
        api.get('/classes', { params: classParams }),
      ]);
      setItems(aRes.data?.data ?? []);
      setClasses(cRes.data?.data ?? []);
    } catch (err) {
      setItems([]);
      setClasses([]);
      toast({
        title: 'Failed to load',
        description: err.response?.data?.message || 'Could not load assignments or classes',
        variant: 'destructive',
      });
    }
  }, [getSelectedAcademicYearId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [load]);

  const isTeacher = user?.role_slug === 'teacher';

  useEffect(() => {
    if (!form.class_id) {
      setSubjects([]);
      return;
    }
    const academicYearId = getSelectedAcademicYearId();
    const params = academicYearId ? { academic_year_id: academicYearId } : {};
    api.get(`/classes/${form.class_id}/subjects`, { params })
      .then((res) => setSubjects(res.data?.data ?? []))
      .catch(() => setSubjects([]));
  }, [form.class_id, getSelectedAcademicYearId]);

  const loadSubmissions = async (assignment) => {
    setSelectedAssignment(assignment);
    setSubmissionsLoading(true);
    try {
      const res = await api.get(`/assignments/${assignment.id}/submissions`);
      setSubmissions(res.data?.data ?? []);
    } catch (e) {
      setSubmissions([]);
      toast({ title: 'Error', description: e.response?.data?.message || 'Could not load submissions', variant: 'destructive' });
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const openReview = (row) => {
    if (!row.submission) return;
    setReviewTarget(row);
    setReviewForm({
      marks: row.submission.marks != null ? String(row.submission.marks) : '',
      feedback: row.submission.feedback || '',
    });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewTarget?.submission || !selectedAssignment) return;
    setReviewSubmitting(true);
    try {
      await api.post(`/assignments/${selectedAssignment.id}/submissions/${reviewTarget.submission.id}/review`, {
        marks: reviewForm.marks !== '' ? Number(reviewForm.marks) : null,
        feedback: reviewForm.feedback || null,
      });
      toast({ title: 'Reviewed', description: 'Submission marked as reviewed.' });
      setReviewTarget(null);
      await loadSubmissions(selectedAssignment);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Review failed', variant: 'destructive' });
    } finally {
      setReviewSubmitting(false);
    }
  };

  const openFile = async (fileId, fileName) => {
    try {
      await downloadAuthenticatedUrl(
        `${API_BASE}/api/assignments/files/${fileId}/download`,
        fileName || 'file',
      );
    } catch {
      toast({ title: 'Error', description: 'Could not open file', variant: 'destructive' });
    }
  };

  const openAssignmentAttachment = async (assignmentId, fileName) => {
    try {
      await downloadAuthenticatedUrl(
        `${API_BASE}/api/assignments/${assignmentId}/attachment`,
        fileName || 'question-sheet',
      );
    } catch {
      toast({ title: 'Error', description: 'Could not open file', variant: 'destructive' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const dtErr = validateDateTime(form.due_date, { required: true });
    if (dtErr) {
      setDueDateError(dtErr);
      toast({ title: 'Invalid due date', description: dtErr, variant: 'destructive' });
      return;
    }
    setDueDateError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('class_id', String(Number(form.class_id)));
      formData.append('subject_id', String(Number(form.subject_id)));
      formData.append('due_date', form.due_date);
      if (form.description) formData.append('description', form.description);
      if (attachmentFile) {
        formData.append('attachment', await prepareFileForUpload(attachmentFile));
      }
      await api.post('/assignments', formData);
      toast({ title: 'Created', description: 'Assignment posted to class.' });
      setForm({ title: '', class_id: '', subject_id: '', due_date: '', description: '' });
      setAttachmentFile(null);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const progressLabel = (sub) => {
    if (!sub) return 'Not started';
    if (sub.status === 'reviewed' || sub.status === 'graded') return 'Reviewed';
    return (sub.progress_status || 'pending').replace('_', ' ');
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <h1 className="text-2xl font-bold">Assignments</h1>
      <Card>
        <CardHeader><CardTitle>Create assignment</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div>
              <Label>Class</Label>
              <select
                className="w-full border rounded-md h-10 px-2 bg-background"
                value={String(form.class_id)}
                onChange={(e) => setForm({ ...form, class_id: e.target.value, subject_id: '' })}
                required
              >
                <option value="">Select class</option>
                {classes.map((c) => (
                  <option key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Subject</Label>
              <select
                className="w-full border rounded-md h-10 px-2 bg-background"
                value={String(form.subject_id)}
                onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                required
                disabled={!form.class_id}
              >
                <option value="">Select subject</option>
                {subjects.map((s) => (
                  <option key={s.id} value={String(s.id)}>{formatLabel(s.name, s)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Due date</Label>
              <DateTimeInput value={form.due_date} onChange={(v) => { setForm({ ...form, due_date: v }); setDueDateError(''); }} error={dueDateError} required />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="assignment-attachment">Question sheet / document (optional)</Label>
              <Input
                id="assignment-attachment"
                ref={attachmentInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                className="bg-background"
              />
              {attachmentFile ? (
                <p className="text-xs text-muted-foreground mt-1">{attachmentFile.name}</p>
              ) : null}
            </div>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Posting…' : 'Post assignment'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recent assignments</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          {items.length === 0 ? <p className="text-muted-foreground">No assignments yet.</p> : null}
          {items.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`w-full text-left border rounded-md p-3 hover:bg-muted/50 transition ${selectedAssignment?.id === a.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => loadSubmissions(a)}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium flex items-center gap-2 flex-wrap">
                  {a.title}
                  <BranchLabel name={a.branch_name} show={showBranchLabels} />
                </p>
                <div className="flex items-center gap-2">
                  {a.has_attachment ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      title="Download question sheet"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAssignmentAttachment(a.id, a.attachment_original_name);
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  ) : null}
                  <Badge variant="secondary">{a.submitted_count ?? 0} submitted</Badge>
                </div>
              </div>
              <p className="text-muted-foreground">{a.class_name} · {a.subject_name} · due {a.due_date}</p>
            </button>
          ))}
        </CardContent>
      </Card>

      {selectedAssignment ? (
        <Card>
          <CardHeader>
            <CardTitle>Submissions — {selectedAssignment.title}</CardTitle>
          </CardHeader>
          <CardContent>
            {submissionsLoading ? <p className="text-muted-foreground">Loading…</p> : null}
            {!submissionsLoading && submissions.length === 0 ? (
              <p className="text-muted-foreground">No students in this class.</p>
            ) : null}
            <div className="space-y-2">
              {submissions.map((row) => (
                <div key={row.student_id} className="flex flex-wrap items-center justify-between gap-2 border rounded-md p-3">
                  <div>
                    <p className="font-medium">{row.student_name}</p>
                    <p className="text-muted-foreground text-xs">
                      {progressLabel(row.submission)}
                      {row.submission?.marks != null ? ` · ${row.submission.marks} marks` : ''}
                      {row.submission?.files?.length ? ` · ${row.submission.files.length} file(s)` : ''}
                    </p>
                  </div>
                  {row.submission ? (
                    <Button size="sm" variant="outline" onClick={() => openReview(row)}>
                      {row.submission.status === 'reviewed' ? 'View review' : 'Review'}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No submission</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={!!reviewTarget} onOpenChange={(open) => !open && setReviewTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review — {reviewTarget?.student_name}</DialogTitle>
          </DialogHeader>
          {reviewTarget?.submission ? (
            <form onSubmit={submitReview} className="space-y-4">
              {reviewTarget.submission.submission_text ? (
                <div>
                  <Label>Student notes</Label>
                  <p className="text-sm border rounded-md p-2 whitespace-pre-wrap">{reviewTarget.submission.submission_text}</p>
                </div>
              ) : null}
              {reviewTarget.submission.files?.length > 0 ? (
                <div className="space-y-1">
                  <Label>Files</Label>
                  {reviewTarget.submission.files.map((f) => (
                    <div key={f.id} className="flex items-center justify-between text-sm border rounded px-2 py-1">
                      <span className="truncate">{f.original_name}</span>
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => openFile(f.id, f.original_name)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
              <div>
                <Label>Marks (max {selectedAssignment?.max_marks ?? 100})</Label>
                <Input
                  type="number"
                  min={0}
                  max={selectedAssignment?.max_marks ?? 100}
                  value={reviewForm.marks}
                  onChange={(e) => setReviewForm((f) => ({ ...f, marks: e.target.value }))}
                  disabled={reviewTarget.submission.status === 'reviewed'}
                />
              </div>
              <div>
                <Label>Feedback</Label>
                <Textarea
                  value={reviewForm.feedback}
                  onChange={(e) => setReviewForm((f) => ({ ...f, feedback: e.target.value }))}
                  disabled={reviewTarget.submission.status === 'reviewed'}
                />
              </div>
              {reviewTarget.submission.status !== 'reviewed' ? (
                <Button type="submit" disabled={reviewSubmitting}>
                  {reviewSubmitting ? 'Saving…' : 'Mark as reviewed'}
                </Button>
              ) : null}
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
